import React, { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Video, Send } from "lucide-react";
import api from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent — we'll be in touch!");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="bg-[#003B5C] py-28 lg:py-36 relative grain-overlay">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-6">Contact</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] text-white max-w-4xl">
            We would love<br /><span className="text-[#D4A437]">to meet you.</span>
          </h1>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Info */}
            <div className="lg:col-span-5 space-y-6">
              <div className="bg-[#F7F8FA] border border-gray-100 p-7 rounded-sm">
                <div className="flex items-start gap-4">
                  <MapPin size={20} className="text-[#D4A437] mt-1" />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#D4A437] font-bold mb-1">Visit</div>
                    <div className="font-display text-lg font-bold text-[#003B5C]">271 Walton St, Richardson, TX 75081</div>
                    <div className="text-sm text-[#1E1E1E]/65 mt-1">Please park at Lot C</div>
                  </div>
                </div>
              </div>

              <div className="bg-[#F7F8FA] border border-gray-100 p-7 rounded-sm">
                <div className="flex items-start gap-4">
                  <Phone size={20} className="text-[#D4A437] mt-1" />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#D4A437] font-bold mb-1">Call</div>
                    <a href="tel:6825647119" data-testid="contact-phone-link" className="font-display text-lg font-bold text-[#003B5C] hover:underline">682-564-7119</a>
                  </div>
                </div>
              </div>

              <div className="bg-[#F7F8FA] border border-gray-100 p-7 rounded-sm">
                <div className="flex items-start gap-4">
                  <Mail size={20} className="text-[#D4A437] mt-1" />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#D4A437] font-bold mb-1">Email</div>
                    <a href="mailto:egsda01@gmail.com" data-testid="contact-email-link" className="font-display text-lg font-bold text-[#003B5C] hover:underline break-all">egsda01@gmail.com</a>
                  </div>
                </div>
              </div>

              <div className="bg-[#003B5C] text-white p-7 rounded-sm">
                <div className="flex items-start gap-4">
                  <Video size={20} className="text-[#D4A437] mt-1" />
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#D4A437] font-bold mb-1">Zoom Worship</div>
                    <div className="text-sm space-y-1 mt-2">
                      <div>Meeting ID: <span className="font-mono">861 0700 4821</span></div>
                      <div>Passcode: <span className="font-mono">2022</span></div>
                    </div>
                  </div>
                </div>
              </div>

              <div data-testid="contact-map" className="rounded-sm overflow-hidden border border-gray-100 h-72">
                <iframe
                  title="Map"
                  src="https://www.google.com/maps?q=271+Walton+St,+Richardson,+TX+75081&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                />
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-7">
              <div className="bg-white border border-gray-200 rounded-sm p-8 sm:p-10">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4A437] mb-3">Send a Message</div>
                <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#003B5C] leading-tight mb-8">
                  Drop us a line.
                </h2>
                <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#1E1E1E]/60 mb-2">Name *</label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={change("name")}
                        data-testid="contact-input-name"
                        className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-[#003B5C] bg-white text-[#1E1E1E]"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#1E1E1E]/60 mb-2">Phone</label>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={change("phone")}
                        data-testid="contact-input-phone"
                        className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-[#003B5C] bg-white text-[#1E1E1E]"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#1E1E1E]/60 mb-2">Email *</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={change("email")}
                      data-testid="contact-input-email"
                      className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-[#003B5C] bg-white text-[#1E1E1E]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#1E1E1E]/60 mb-2">Message *</label>
                    <textarea
                      rows={6}
                      value={form.message}
                      onChange={change("message")}
                      data-testid="contact-input-message"
                      className="w-full px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:border-[#003B5C] bg-white text-[#1E1E1E] resize-none"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-button"
                    className="inline-flex items-center gap-2 bg-[#003B5C] text-white px-8 py-4 rounded-sm font-medium hover:bg-[#002A42] transition-all disabled:opacity-50"
                  >
                    {submitting ? "Sending…" : (<>Send Message <Send size={16} /></>)}
                  </button>
                </form>

                <div data-testid="contact-backup-info" className="mt-8 pt-6 border-t border-gray-100 text-sm text-[#1E1E1E]/70">
                  For direct contact, email us at{" "}
                  <a
                    href="mailto:egsda01@gmail.com"
                    data-testid="contact-backup-email"
                    className="font-semibold text-[#003B5C] underline decoration-[#D4A437] decoration-2 underline-offset-4 hover:text-[#002A42]"
                  >
                    egsda01@gmail.com
                  </a>
                  .
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
